import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import styles from "./CheckLists.module.css";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";
import { ItemFila } from "../../types/types";

interface Checklist {
  uuid: string;
  nomeArquivo: string;
  tamanho: number;
  dataUpload: string;
  nomeUsuarioUpload: string;
  status: "Pendente" | "Analisado";
  nomeUsuarioAnalise?: string | null;
  dataAnalise?: string | null;
  observacao?: string | null;
}

const DIAS_SEMANA = ["D", "S", "T", "Q", "Q", "S", "S"];
const DIA_ENTREGA = 4; // quinta-feira

function formatarTamanho(bytes: number): string {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatarData(data?: string | null): string {
  if (!data) return "—";
  return new Date(data)
    .toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", " às");
}

function gerarIdFila(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const CheckLists = () => {
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarregar, setErroCarregar] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [filtro, setFiltro] = useState<"todos" | "Pendente" | "Analisado">(
    "todos",
  );
  const [fila, setFila] = useState<ItemFila[]>([]);
  const [processandoFila, setProcessandoFila] = useState(false);
  const [modalAnalise, setModalAnalise] = useState<Checklist | null>(null);
  const [observacao, setObservacao] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const token = useSelector((state: RootState) => state.auth.token);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );
  const dispatch = useDispatch();

  useSessionExpirationCheck(sessionExpiration, token);
  const usuario = useSelector((state: RootState) => state.auth.user);

  const carregarChecklists = useCallback(async () => {
    setCarregando(true);
    setErroCarregar(false);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/api/checklist`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          setChecklists([]);
          return;
        }
        throw new Error("Não foi possível carregar os checklists.");
      }

      const data = await response.json();
      setChecklists(data);
    } catch (error) {
      if (error instanceof Error) console.error(error.message);
      setErroCarregar(true);
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/area-do-cliente");
      return;
    }
    carregarChecklists();
    document.title = "Íkone | Checklists";
  }, [token, navigate, carregarChecklists]);

  function adicionarArquivosNaFila(arquivos: FileList | File[]) {
    const validos: ItemFila[] = [];

    for (const arquivo of Array.from(arquivos)) {
      if (!/\.(xlsx|xls)$/i.test(arquivo.name)) {
        toast.error(
          `"${arquivo.name}" não é um Excel (.xlsx/.xls) e foi ignorado.`,
        );
        continue;
      }
      validos.push({ id: gerarIdFila(), arquivo, status: "pendente" });
    }

    if (validos.length === 0) return;

    setFila((atual) => [...atual, ...validos]);
  }

  function atualizarItemFila(id: string, patch: Partial<ItemFila>) {
    setFila((atual) =>
      atual.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  }

  function removerDaFila(id: string) {
    setFila((atual) => atual.filter((item) => item.id !== id));
  }

  function limparFilaFinalizada() {
    setFila((atual) => atual.filter((item) => item.status !== "sucesso"));
  }

  async function enviarArquivoDaFila(item: ItemFila) {
    if (!usuario) {
      atualizarItemFila(item.id, {
        status: "erro",
        erro: "Não foi possível identificar o usuário logado.",
      });
      return;
    }

    atualizarItemFila(item.id, { status: "enviando", erro: undefined });

    const formData = new FormData();
    formData.append("arquivo", item.arquivo);
    formData.append("idUsuarioUpload", String(usuario.id));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/api/checklist/upload`,
        {
          method: "POST",
          body: formData,
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        atualizarItemFila(item.id, { status: "sucesso" });
      } else {
        atualizarItemFila(item.id, {
          status: "erro",
          erro: "Erro ao enviar checklist.",
        });
      }
    } catch (error) {
      atualizarItemFila(item.id, {
        status: "erro",
        erro: "Erro de conexão ao enviar.",
      });
    }
  }

  useEffect(() => {
    if (processandoFila) return;

    const proximo = fila.find((item) => item.status === "pendente");
    if (!proximo) return;

    setProcessandoFila(true);
    enviarArquivoDaFila(proximo).finally(() => {
      setProcessandoFila(false);
    });
  }, [fila, processandoFila]);

  useEffect(() => {
    const aindaTemAlgoRodando = fila.some(
      (item) => item.status === "pendente" || item.status === "enviando",
    );
    const teveAlgumSucesso = fila.some((item) => item.status === "sucesso");

    if (!aindaTemAlgoRodando && teveAlgumSucesso) {
      carregarChecklists();
    }
  }, [fila, carregarChecklists]);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setArrastando(false);
    if (e.dataTransfer.files?.length) {
      adicionarArquivosNaFila(e.dataTransfer.files);
    }
  };

  const handleBaixar = async (checklist: Checklist) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/api/checklist/download/${checklist.uuid}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!response.ok) throw new Error("Erro ao baixar o arquivo");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = checklist.nomeArquivo;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof Error) toast.error(error.message);
    }
  };

  const handleApagar = async (checklist: Checklist) => {
    const confirmacao = window.confirm(
      `Apagar o checklist "${checklist.nomeArquivo}"? Essa ação não pode ser desfeita.`,
    );
    if (!confirmacao) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/api/checklist/${checklist.uuid}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.ok) {
        toast.success("Checklist apagado com sucesso.");
        setChecklists((prev) => prev.filter((c) => c.uuid !== checklist.uuid));
      } else {
        toast.error("Erro ao apagar o checklist.");
      }
    } catch (error) {
      if (error instanceof Error) console.error(error.message);
    }
  };

  const abrirModalAnalise = (checklist: Checklist) => {
    setModalAnalise(checklist);
    setObservacao("");
  };

  const confirmarAnalise = async () => {
    if (!modalAnalise || !usuario) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/api/checklist/analisar/${modalAnalise.uuid}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ID_USUARIO_ANALISE: usuario.id,
            Observacao: observacao,
          }),
        },
      );

      if (response.ok) {
        toast.success("Checklist analisado com sucesso.");
        setModalAnalise(null);
        carregarChecklists();
      } else {
        toast.error("Erro ao registrar a análise.");
      }
    } catch (error) {
      if (error instanceof Error) console.error(error.message);
      toast.error("Erro ao registrar a análise.");
    }
  };

  const listaFiltrada = checklists.filter(
    (c) => filtro === "todos" || c.status === filtro,
  );
  const pendentes = checklists.filter((c) => c.status === "Pendente").length;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Checklists semanais</h1>
          <p className={styles.subtitle}>
            Enviar, baixar e analisar os checklists semanais da ÍKONE
          </p>
        </div>

        <div className={styles.weekStrip} aria-hidden="true">
          {DIAS_SEMANA.map((dia, i) => (
            <span
              key={i}
              className={
                i === DIA_ENTREGA ? styles.weekDayActive : styles.weekDay
              }
            >
              {dia}
            </span>
          ))}
        </div>
      </header>

      <section className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statNumber}>{checklists.length}</span>
          <span className={styles.statLabel}>Checklists no histórico</span>
        </div>
        <div
          className={`${styles.statCard} ${pendentes > 0 ? styles.statCardWarning : ""}`}
        >
          <span className={styles.statNumber}>{pendentes}</span>
          <span className={styles.statLabel}>Aguardando análise</span>
        </div>
      </section>

      <section
        className={`${styles.dropzone} ${arrastando ? styles.dropzoneActive : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          multiple
          className={styles.hiddenInput}
          onChange={(e) => {
            if (e.target.files?.length) {
              adicionarArquivosNaFila(e.target.files);
              e.target.value = ""; // permite selecionar os mesmos arquivos de novo depois
            }
          }}
        />
        <div className={styles.dropzoneContent}>
          <p className={styles.dropzoneTitle}>
            {fila.length > 0
              ? "Arraste mais planilhas ou solte aqui"
              : "Arraste as planilhas do checklist aqui"}
          </p>
          <p className={styles.dropzoneHint}>
            Formatos aceitos: .xlsx, .xls — pode selecionar vários de uma vez
          </p>
          <button
            type="button"
            className={styles.buttonPrimary}
            onClick={() => inputRef.current?.click()}
          >
            Escolher arquivos
          </button>
        </div>
      </section>

      {fila.length > 0 && (
        <section className={styles.filaSection}>
          <div className={styles.filaHeader}>
            <h2 className={styles.tableTitle}>
              Enviando ({fila.filter((i) => i.status === "sucesso").length}/
              {fila.length})
            </h2>
            {fila.some((i) => i.status === "sucesso") && (
              <button
                type="button"
                className={styles.filaLimparBtn}
                onClick={limparFilaFinalizada}
              >
                Limpar concluídos
              </button>
            )}
          </div>

          <ul className={styles.filaLista}>
            {fila.map((item) => (
              <li key={item.id} className={styles.filaItem}>
                <span className={styles.filaNome}>{item.arquivo.name}</span>
                <span className={styles.filaTamanho}>
                  {formatarTamanho(item.arquivo.size)}
                </span>

                {item.status === "pendente" && (
                  <span className={styles.filaStatusPendente}>Na fila…</span>
                )}
                {item.status === "enviando" && (
                  <span className={styles.filaStatusEnviando}>Enviando…</span>
                )}
                {item.status === "sucesso" && (
                  <span className={styles.filaStatusSucesso}>✓ Enviado</span>
                )}
                {item.status === "erro" && (
                  <span className={styles.filaStatusErro} title={item.erro}>
                    ✕ Falhou
                  </span>
                )}

                {item.status === "erro" && (
                  <button
                    type="button"
                    className={styles.filaRetryBtn}
                    onClick={() =>
                      atualizarItemFila(item.id, {
                        status: "pendente",
                        erro: undefined,
                      })
                    }
                  >
                    Tentar de novo
                  </button>
                )}

                {(item.status === "pendente" || item.status === "erro") && (
                  <button
                    type="button"
                    className={styles.filaRemoverBtn}
                    onClick={() => removerDaFila(item.id)}
                    aria-label={`Remover ${item.arquivo.name} da fila`}
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={styles.tableSection}>
        <div className={styles.tableToolbar}>
          <h2 className={styles.tableTitle}>Histórico de envios</h2>
          <div className={styles.filterGroup}>
            {(["todos", "Pendente", "Analisado"] as const).map((opcao) => (
              <button
                key={opcao}
                className={
                  filtro === opcao
                    ? styles.filterButtonActive
                    : styles.filterButton
                }
                onClick={() => setFiltro(opcao)}
              >
                {opcao === "todos" ? "Todos" : opcao}
              </button>
            ))}
          </div>
        </div>

        {carregando ? (
          <div className={styles.emptyState}>Carregando checklists…</div>
        ) : erroCarregar ? (
          <p className={styles.errorText}>
            Houve um erro ao buscar os checklists.
          </p>
        ) : listaFiltrada.length === 0 ? (
          <div className={styles.emptyState}>
            Nenhum checklist encontrado
            {filtro !== "todos" ? ` com status "${filtro}"` : ""}.
          </div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Arquivo</th>
                  <th>Enviado por</th>
                  <th>Data de envio</th>
                  <th>Status</th>
                  <th>Analisado por</th>
                  <th>Data da análise</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {listaFiltrada.map((c) => (
                  <tr key={c.uuid}>
                    <td>
                      <span className={styles.fileName}>{c.nomeArquivo}</span>
                      <span className={styles.fileSize}>
                        {formatarTamanho(c.tamanho)}
                      </span>
                    </td>
                    <td>{c.nomeUsuarioUpload}</td>
                    <td>{formatarData(c.dataUpload)}</td>
                    <td>
                      <span
                        className={
                          c.status === "Analisado"
                            ? styles.badgeSuccess
                            : styles.badgePending
                        }
                      >
                        {c.status}
                      </span>
                    </td>
                    <td>{c.nomeUsuarioAnalise ?? "—"}</td>
                    <td>{formatarData(c.dataAnalise)}</td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.iconButton}
                          onClick={() => handleBaixar(c)}
                        >
                          Baixar
                        </button>
                        {c.status === "Pendente" && (
                          <button
                            className={styles.iconButtonPrimary}
                            onClick={() => abrirModalAnalise(c)}
                          >
                            Analisar
                          </button>
                        )}
                        <button
                          className={styles.iconButtonDanger}
                          onClick={() => handleApagar(c)}
                        >
                          Apagar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className={styles.cardList}>
              {listaFiltrada.map((c) => (
                <div key={c.uuid} className={styles.card}>
                  <div className={styles.cardHeader}>
                    <span className={styles.fileName}>{c.nomeArquivo}</span>
                    <span
                      className={
                        c.status === "Analisado"
                          ? styles.badgeSuccess
                          : styles.badgePending
                      }
                    >
                      {c.status}
                    </span>
                  </div>
                  <dl className={styles.cardDetails}>
                    <div>
                      <dt>Enviado por</dt>
                      <dd>{c.nomeUsuarioUpload}</dd>
                    </div>
                    <div>
                      <dt>Data de envio</dt>
                      <dd>{formatarData(c.dataUpload)}</dd>
                    </div>
                    <div>
                      <dt>Analisado por</dt>
                      <dd>{c.nomeUsuarioAnalise ?? "—"}</dd>
                    </div>
                    <div>
                      <dt>Data da análise</dt>
                      <dd>{formatarData(c.dataAnalise)}</dd>
                    </div>
                  </dl>
                  <div className={styles.actions}>
                    <button
                      className={styles.iconButton}
                      onClick={() => handleBaixar(c)}
                    >
                      Baixar
                    </button>
                    {c.status === "Pendente" && (
                      <button
                        className={styles.iconButtonPrimary}
                        onClick={() => abrirModalAnalise(c)}
                      >
                        Analisar
                      </button>
                    )}
                    <button
                      className={styles.iconButtonDanger}
                      onClick={() => handleApagar(c)}
                    >
                      Apagar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <Modal
        show={!!modalAnalise}
        onHide={() => setModalAnalise(null)}
        centered
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <div>
            <Modal.Title className={styles.modalTitle}>
              Marcar como analisado
            </Modal.Title>
            <div className={styles.modalSubtitle}>
              {modalAnalise?.nomeArquivo}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className={styles.modalBody}>
          <label className={styles.modalLabel} htmlFor="observacao">
            Observação (opcional)
          </label>
          <textarea
            id="observacao"
            className={styles.modalTextarea}
            rows={4}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="Alguma pendência ou observação sobre esse checklist?"
          />
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <button
            type="button"
            className={styles.buttonSecondary}
            onClick={() => setModalAnalise(null)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.buttonPrimary}
            onClick={confirmarAnalise}
          >
            Confirmar análise
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CheckLists;
